import {FC, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {KTSVG} from '../../../../_metronic/helpers'
import {showErrorAlert} from '../../../helpers/alert'
import {handleApiError} from '../../../utils/handleApiError'
import {useConfirmAction} from '../../../hooks/utils/useConfirmAction'
import {convertSuggestion, updateSuggestionStatus} from '../../../services/suggestionService'
import {canConvert, nextStatuses, statusColor, statusLabel, SuggestionStatus} from '../suggestions'

type Props = {
  suggestion: any | null
  onClose: () => void
  // Called after a successful status change / convert so the list can refresh.
  onChanged: () => void
}

const Row: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div className='mb-4'>
    <div className='text-muted fs-8 text-uppercase mb-1'>{label}</div>
    <div>{children}</div>
  </div>
)

// Staff detail view for one suggestion: full fields + status-transition actions + convert-to-
// experience. Convert is offered while the idea is still in intake (received / under_review).
export const SuggestionDetailModal: FC<Props> = ({suggestion, onClose, onChanged}) => {
  const confirm = useConfirmAction()
  const [busy, setBusy] = useState(false)

  if (!suggestion) return null

  const id: number = suggestion.id
  const submittedBy = suggestion.submittedBy?.name || suggestion.submittedBy?.email || '—'

  const runTransition = async (to: SuggestionStatus) => {
    const ok = await confirm({
      title: `Move to ${statusLabel(to)}`,
      message: `Set this suggestion's status to "${statusLabel(to)}"?`,
    })
    if (!ok) return
    try {
      setBusy(true)
      await updateSuggestionStatus(id, to)
      showErrorAlert({title: 'Updated', message: `Status set to ${statusLabel(to)}.`})
      onChanged()
      onClose()
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({title: 'Error', message: 'Could not update the status right now.'}),
      })
    } finally {
      setBusy(false)
    }
  }

  const runConvert = async () => {
    const ok = await confirm({
      title: 'Convert to experience',
      message: 'Create a draft experience from this suggestion and mark it Converted?',
      btnTextAction: 'Convert',
    })
    if (!ok) return
    try {
      setBusy(true)
      await convertSuggestion(id)
      showErrorAlert({title: 'Converted', message: 'A draft experience was created.'})
      onChanged()
      onClose()
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({title: 'Error', message: 'Could not convert this suggestion right now.'}),
      })
    } finally {
      setBusy(false)
    }
  }

  const transitions = nextStatuses(suggestion.status)

  return (
    <Modal show centered backdrop='static' onHide={onClose} size='lg'>
      <div className='p-8'>
        <div className='d-flex justify-content-between align-items-start mb-6'>
          <h2 className='nb-invite-modal__title mb-0'>{suggestion.title || 'Untitled'}</h2>
          <div className='cursor-pointer' onClick={onClose}>
            <KTSVG path='/media/svg/nobilis/closeX.svg' />
          </div>
        </div>

        <div className='d-flex flex-wrap gap-8'>
          <Row label='Status'>
            <span
              className='badge'
              style={{backgroundColor: statusColor(suggestion.status), color: '#fff'}}
            >
              {suggestion.statusDisplay || statusLabel(suggestion.status)}
            </span>
          </Row>
          <Row label='Submitted by'>{submittedBy}</Row>
          <Row label='Category'>{suggestion.category || '—'}</Row>
          <Row label='Location'>{suggestion.location || '—'}</Row>
          <Row label='Wants to lead'>{suggestion.wantsToLead ? 'Yes' : 'No'}</Row>
        </div>

        <Row label='Description'>{suggestion.description || '—'}</Row>
        <Row label='Why this experience'>{suggestion.why || '—'}</Row>

        <div className='d-flex flex-wrap gap-3 mt-6 pt-6 border-top'>
          {canConvert(suggestion.status) && (
            <button
              type='button'
              className='btn nb-btn-primary'
              disabled={busy}
              onClick={runConvert}
            >
              Convert to experience
            </button>
          )}
          {transitions.map((to) => (
            <button
              key={to}
              type='button'
              className='btn btn-outline btn-outline-dark'
              disabled={busy}
              onClick={() => runTransition(to)}
            >
              {statusLabel(to)}
            </button>
          ))}
          <button type='button' className='btn-secondary ms-auto' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}
